import { useState, useEffect } from "react";

interface LeaderboardEntry {
  address: string;
  tier: string;
  daysHeld: number;
  points: number;
}

interface LoyaltyData {
  leaderboard: LeaderboardEntry[];
  totalPoints: number;
  poolBalancePC: string;
  distributionThreshold: string;
  distributionPct: number;
  holderCount: number;
}

const LoyaltyPage = () => {
  const [data, setData] = useState<LoyaltyData | null>(null);

  useEffect(() => {
    fetch("/api/loyalty")
      .then((r) => r.json())
      .then(setData)
      .catch(console.error);
  }, []);

  const poolPC = parseFloat(data?.poolBalancePC || "0");
  const threshold = parseFloat(data?.distributionThreshold || "100");
  const pct = Math.min(100, Math.round((poolPC / threshold) * 100));

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto", padding: "40px 20px", fontFamily: "Avenir, Helvetica, Arial, sans-serif", color: "white" }}>
      <a href="/" style={{ color: "rgb(231, 98, 243)", textDecoration: "none", fontSize: "13px" }}>← Back to Faucet</a>

      <h1 style={{ fontSize: "28px", marginTop: "20px", marginBottom: "4px" }}>💎 Loyalty Rewards</h1>
      <p style={{ opacity: 0.7, fontSize: "14px", marginTop: 0 }}>
        Hold PEN on Pentagon Chain. Accumulate points. Earn bonus distributions.
      </p>

      {/* How it works */}
      <div style={{ background: "rgba(255, 215, 0, 0.05)", border: "1px solid rgba(255, 215, 0, 0.2)", borderRadius: "8px", padding: "20px", marginBottom: "24px" }}>
        <h2 style={{ fontSize: "18px", margin: "0 0 12px", color: "#FFD700" }}>How It Works</h2>

        <div style={{ fontSize: "13px", lineHeight: "22px", opacity: 0.85 }}>
          <p style={{ margin: "0 0 10px" }}>
            <strong>Earn points</strong> by holding PEN on Pentagon Chain continuously. Points accumulate daily based on your on-chain VIP tier:
          </p>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginBottom: "16px" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255, 215, 0, 0.15)" }}>
                <th style={{ padding: "6px 12px", textAlign: "left" }}>On-chain Tier</th>
                <th style={{ padding: "6px 12px", textAlign: "right" }}>PEN Required</th>
                <th style={{ padding: "6px 12px", textAlign: "right" }}>Points/Day</th>
                <th style={{ padding: "6px 12px", textAlign: "right" }}>Points/Month</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                <td style={{ padding: "6px 12px" }}>VIP1</td>
                <td style={{ padding: "6px 12px", textAlign: "right" }}>200,000</td>
                <td style={{ padding: "6px 12px", textAlign: "right", color: "#FFD700" }}>1</td>
                <td style={{ padding: "6px 12px", textAlign: "right", color: "#FFD700" }}>~30</td>
              </tr>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                <td style={{ padding: "6px 12px" }}>VIP2</td>
                <td style={{ padding: "6px 12px", textAlign: "right" }}>1,000,000</td>
                <td style={{ padding: "6px 12px", textAlign: "right", color: "#FFD700" }}>10</td>
                <td style={{ padding: "6px 12px", textAlign: "right", color: "#FFD700" }}>~300</td>
              </tr>
              <tr>
                <td style={{ padding: "6px 12px" }}>VIP3</td>
                <td style={{ padding: "6px 12px", textAlign: "right" }}>5,000,000</td>
                <td style={{ padding: "6px 12px", textAlign: "right", color: "#FFD700" }}>50</td>
                <td style={{ padding: "6px 12px", textAlign: "right", color: "#FFD700" }}>~1,500</td>
              </tr>
            </tbody>
          </table>

          <p style={{ margin: "0 0 10px" }}>
            <strong>Upgrading your tier</strong> is rewarded. If you start as VIP1 and upgrade to VIP2, your points are recalculated at the higher rate for your entire holding period.
          </p>

          <p style={{ margin: "0 0 10px" }}>
            <strong>Moving or selling PEN</strong> resets your points to zero and triggers a 7-day cooldown before you can regain VIP status.
          </p>
        </div>
      </div>

      {/* Distribution mechanics */}
      <div style={{ background: "rgba(231, 98, 243, 0.05)", border: "1px solid rgba(231, 98, 243, 0.2)", borderRadius: "8px", padding: "20px", marginBottom: "24px" }}>
        <h2 style={{ fontSize: "18px", margin: "0 0 12px", color: "rgb(231, 98, 243)" }}>Pool Distribution Events</h2>

        <div style={{ fontSize: "13px", lineHeight: "22px", opacity: 0.85 }}>
          <p style={{ margin: "0 0 10px" }}>
            When the faucet pool reaches <strong>{data?.distributionThreshold || "100"} PC</strong>, a distribution event is triggered:
          </p>
          <ul style={{ margin: "0 0 10px", paddingLeft: "20px" }}>
            <li><strong>{data?.distributionPct || 50}%</strong> of the pool is distributed to holders based on loyalty points</li>
            <li>Remaining 50% stays in the pool for daily faucet claims</li>
            <li>Each holder receives: <code>(their points / total points) × distribution amount</code></li>
            <li>All loyalty points reset after distribution (new cycle begins)</li>
          </ul>
        </div>

        {/* Pool progress toward threshold */}
        <div style={{ marginTop: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            <span style={{ fontSize: "12px", opacity: 0.6 }}>Pool Progress</span>
            <span style={{ fontSize: "12px" }}>{poolPC.toFixed(2)} / {threshold} PC</span>
          </div>
          <div style={{ width: "100%", height: "8px", borderRadius: "4px", background: "rgba(255,255,255,0.1)" }}>
            <div style={{
              width: `${pct}%`,
              height: "100%",
              borderRadius: "4px",
              background: "linear-gradient(90deg, rgb(96, 64, 239), rgb(231, 98, 243))",
              transition: "width 0.3s",
            }} />
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div style={{ background: "rgba(27, 26, 27, 0.5)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "8px", padding: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <h2 style={{ fontSize: "18px", margin: 0 }}>Loyalty Leaderboard</h2>
          <span style={{ fontSize: "12px", opacity: 0.5 }}>{data?.holderCount || 0} holders</span>
        </div>

        {data?.leaderboard && data.leaderboard.length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                <th style={{ padding: "8px 12px", textAlign: "left" }}>#</th>
                <th style={{ padding: "8px 12px", textAlign: "left" }}>Address</th>
                <th style={{ padding: "8px 12px", textAlign: "right" }}>Tier</th>
                <th style={{ padding: "8px 12px", textAlign: "right" }}>Days</th>
                <th style={{ padding: "8px 12px", textAlign: "right" }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {data.leaderboard.map((entry, i) => (
                <tr key={entry.address} style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                  <td style={{ padding: "6px 12px", color: i < 3 ? "#FFD700" : "inherit" }}>
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                  </td>
                  <td style={{ padding: "6px 12px", fontFamily: "monospace", fontSize: "12px" }}>
                    {entry.address.slice(0, 6)}...{entry.address.slice(-4)}
                  </td>
                  <td style={{ padding: "6px 12px", textAlign: "right", color: "rgb(231, 98, 243)" }}>
                    {entry.tier}
                  </td>
                  <td style={{ padding: "6px 12px", textAlign: "right" }}>
                    {entry.daysHeld}
                  </td>
                  <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: "bold", color: "#FFD700" }}>
                    {entry.points.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ fontSize: "13px", opacity: 0.5, textAlign: "center", padding: "20px 0" }}>
            No holders yet. Be the first to bridge PEN to Pentagon Chain!
          </p>
        )}

        {data?.totalPoints ? (
          <p style={{ fontSize: "11px", opacity: 0.4, marginTop: "12px", marginBottom: 0, textAlign: "right" }}>
            Total points across all holders: {data.totalPoints.toLocaleString()}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default LoyaltyPage;
